import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  WifiIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useUserStore } from '../../store/userStore';
import { userApi } from '../../services/api';

// 타입 정의
interface Room {
  id: string;
  name: string;
  room_type: string;
  grid_width: number;
  grid_height: number;
}

interface Seat {
  seat_number: number;
  room_id: string;
  position_x: number;
  position_y: number;
  zone_id: string;
  is_available: boolean;
  is_out_of_order: boolean;
  direction: 'north' | 'south' | 'east' | 'west';
  is_active: boolean;
}

interface Zone {
  id: string;
  name: string;
  room_id: string;
  color: string;
}

interface Device {
  id: string;
  room_id: string;
  device_type: 'face_recognition_gate' | 'kiosk' | 'printer' | 'air_purifier' | 'cctv';
  device_name: string;
  position_x: number;
  position_y: number;
  is_active: boolean;
  network_info: {
    ip_address?: string;
    mac_address?: string;
    last_ping?: string;
  };
  status: 'online' | 'offline' | 'warning' | 'error';
  last_seen: string;
  attributes?: {
    direction?: 'entrance' | 'exit' | 'both';
    paper_level?: number;
    recognition_count_today?: number;
  };
}

interface ArchitectureElement {
  id: string;
  room_id: string;
  element_type: 'entrance' | 'entrance_with_gate' | 'emergency_exit' | 'window' | 'pillar';
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  rotation: number;
  wall_position?: 'top' | 'bottom' | 'left' | 'right';
  attributes?: {
    material?: string;
    color?: string;
    opacity?: number;
  };
}

interface StudentLayoutViewProps {
  onSeatSelect?: (seat: Seat) => void;
  selectedSeat?: Seat | null;
}

const CELL_SIZE = 80; // 그리드 셀 크기 (픽셀)

// 건축 요소 렌더링 함수
const renderArchitectureElement = (element: ArchitectureElement) => {
  switch (element.element_type) {
    case 'entrance':
      return (
        <div className="w-full h-full bg-white border-x-2 border-gray-400"
             style={{
               boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
             }}>
          <div className="w-full h-full bg-yellow-200 flex items-center justify-center"
               style={{
                 background: 'linear-gradient(to bottom, #FEF3C7, #FDE68A)'
               }}>
            <span className="text-xs font-medium text-gray-700 drop-shadow-sm">출입구</span>
          </div>
        </div>
      );

    case 'entrance_with_gate':
      return (
        <div className="w-full h-full bg-white border-x-2 border-gray-400 relative"
             style={{
               boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
             }}>
          <div className="w-full h-full bg-yellow-200 flex flex-col items-center justify-center"
               style={{
                 background: 'linear-gradient(to bottom, #FEF3C7, #FDE68A)'
               }}>
            <span className="text-xs font-medium text-gray-700 drop-shadow-sm">출입구</span>
            <div className="w-6 h-4 bg-blue-600 rounded-sm mt-1 flex items-center justify-center">
              <span className="text-xs text-white font-bold">G</span>
            </div>
          </div>
          {/* 게이트 표시선 */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-600 opacity-50"></div>
        </div>
      );
      
    case 'emergency_exit':
      return (
        <div className="w-full h-full bg-white border-x-2 border-gray-400"
             style={{
               boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
             }}>
          <div className="w-full h-full bg-green-200 flex items-center justify-center"
               style={{
                 background: 'linear-gradient(to bottom, #D1FAE5, #A7F3D0)'
               }}>
            <span className="text-xs font-medium text-gray-700 drop-shadow-sm">비상구</span>
          </div>
        </div>
      );
      
    case 'window':
      return (
        <div className="w-full h-full bg-sky-100 border-y-2 border-gray-400"
             style={{
               background: element.wall_position === 'left' 
                 ? 'linear-gradient(to right, rgba(224, 242, 254, 0.8), rgba(186, 230, 253, 0.8))'
                 : element.wall_position === 'right'
                 ? 'linear-gradient(to left, rgba(224, 242, 254, 0.8), rgba(186, 230, 253, 0.8))'
                 : element.wall_position === 'top'
                 ? 'linear-gradient(to bottom, rgba(224, 242, 254, 0.8), rgba(186, 230, 253, 0.8))'
                 : 'linear-gradient(to top, rgba(224, 242, 254, 0.8), rgba(186, 230, 253, 0.8))',
               boxShadow: 'inset 0 0 8px rgba(0,0,0,0.1)'
             }}>
          <div className="w-full h-full flex items-center justify-center relative overflow-hidden">
            {/* 창틀 효과 */}
            {(element.wall_position === 'left' || element.wall_position === 'right') && (
              <>
                <div className="absolute top-0 w-full h-0.5 bg-gray-500"></div>
                <div className="absolute bottom-0 w-full h-0.5 bg-gray-500"></div>
                <div className="absolute top-1/2 -translate-y-1/2 w-full h-0.5 bg-gray-400"></div>
              </>
            )}
            {(element.wall_position === 'top' || element.wall_position === 'bottom') && (
              <>
                <div className="absolute left-0 h-full w-0.5 bg-gray-500"></div>
                <div className="absolute right-0 h-full w-0.5 bg-gray-500"></div>
                <div className="absolute left-1/2 -translate-x-1/2 h-full w-0.5 bg-gray-400"></div>
              </>
            )}
            <span className="text-xs font-medium text-gray-600 drop-shadow-sm z-10 bg-white/70 px-1 rounded">창문</span>
          </div>
        </div>
      );
      
    case 'pillar':
      return (
        <div className="w-full h-full bg-gray-700 rounded-lg flex items-center justify-center"
             style={{
               boxShadow: '0 8px 16px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)',
               background: 'linear-gradient(135deg, #4B5563, #374151)'
             }}>
          <div className="w-full h-full rounded-lg relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-tr from-gray-600 to-gray-800 opacity-50"></div>
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-r from-white/20 to-transparent"></div>
            <div className="absolute top-0 right-0 w-2 h-full bg-gradient-to-l from-black/20 to-transparent"></div>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-300 drop-shadow-lg">기둥</span>
          </div>
        </div>
      );
  }
};

// 좌석 방향에 따른 회전 스타일
const getSeatRotation = (direction: string) => {
  switch (direction) {
    case 'north': return 'rotate-0';
    case 'south': return 'rotate-180';
    case 'east': return 'rotate-90';
    case 'west': return 'rotate-270';
    default: return 'rotate-0';
  }
};

// 기기 아이콘 반환
const getDeviceIcon = (deviceType: string) => {
  switch (deviceType) {
    case 'face_recognition_gate':
      return <div className="text-2xl">🚪</div>;
    case 'kiosk':
      return <div className="text-2xl">💻</div>;
    case 'printer':
      return <div className="text-2xl">🖨️</div>;
    case 'air_purifier':
      return <div className="text-2xl">💨</div>;
    case 'cctv':
      return <div className="text-2xl">📹</div>;
    default:
      return <div className="text-2xl">📟</div>;
  }
};

// 기기 상태에 따른 색상
const getDeviceStatusColor = (status: string) => {
  switch (status) {
    case 'online': return 'border-green-500 bg-green-50';
    case 'warning': return 'border-yellow-500 bg-yellow-50';
    case 'error': return 'border-red-500 bg-red-50';
    default: return 'border-gray-500 bg-gray-50';
  }
};

const StudentLayoutView: React.FC<StudentLayoutViewProps> = ({ onSeatSelect }) => {
  const navigate = useNavigate();
  const { currentUser, setCurrentSession } = useUserStore();
  
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [architectureElements, setArchitectureElements] = useState<ArchitectureElement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // UI 상태
  const [scale, setScale] = useState(1);
  const [viewMode, setViewMode] = useState<'all' | 'seats' | 'devices'>('all');
  const [selectedSeat, setSelectedSeat] = useState<Seat | null>(null);
  const [showActionModal, setShowActionModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
      };

      // 룸 목록 가져오기
      const roomsResponse = await fetch('/v1/seat-config/rooms', { headers });
      if (!roomsResponse.ok) throw new Error('룸 정보를 불러오는데 실패했습니다');
      const roomsData = await roomsResponse.json();
      setRooms(roomsData || []);
      
      // 현재 선택된 룸 유지 또는 첫 번째 룸 선택
      const currentRoomId = selectedRoom || (roomsData && roomsData[0]?.id);
      if (currentRoomId) {
        setSelectedRoom(currentRoomId);
      }

      // 구역 정보 가져오기 (현재 선택된 룸만)
      const zonesUrl = currentRoomId ? `/v1/seat-config/zones?room_id=${currentRoomId}` : '/v1/seat-config/zones';
      const zonesResponse = await fetch(zonesUrl, { headers });
      if (!zonesResponse.ok) throw new Error('구역 정보를 불러오는데 실패했습니다');
      const zonesData = await zonesResponse.json();
      setZones(zonesData || []);

      // 좌석 설정 정보 가져오기 (위치, 구역 등)
      const seatsUrl = currentRoomId ? `/v1/seat-config/seats?room_id=${currentRoomId}` : '/v1/seat-config/seats';
      const seatsResponse = await fetch(seatsUrl, { headers });
      if (!seatsResponse.ok) throw new Error('좌석 정보를 불러오는데 실패했습니다');
      const seatsData = await seatsResponse.json();
      
      // 실시간 좌석 현황 가져오기
      try {
        const realTimeSeatsResponse = await fetch('/v1/owner/seats-management', { headers });
        if (realTimeSeatsResponse.ok) {
          const realTimeData = await realTimeSeatsResponse.json();
          const realTimeSeats = realTimeData.seats || [];
          
          // 좌석 설정과 실시간 상태를 병합
          const mergedSeats = seatsData.map((seatConfig: any) => {
            const realTimeSeat = realTimeSeats.find((rts: any) => rts.seat_number === seatConfig.seat_number);
            return {
              ...seatConfig,
              is_available: realTimeSeat ? (realTimeSeat.is_available && !realTimeSeat.is_out_of_order) : seatConfig.is_available,
              is_out_of_order: realTimeSeat ? realTimeSeat.is_out_of_order : seatConfig.is_out_of_order,
            };
          });
          
          setSeats(mergedSeats);
        } else {
          // 실시간 API 실패 시 기본 좌석 설정만 사용
          setSeats(seatsData || []);
        }
      } catch (realTimeError) {
        console.error('실시간 좌석 상태 로드 실패:', realTimeError);
        setSeats(seatsData || []);
      }

      // 기기 정보 가져오기 (API 구현 필요)
      try {
        const devicesResponse = await fetch('/v1/devices', { headers });
        if (devicesResponse.ok) {
          const devicesData = await devicesResponse.json();
          setDevices(devicesData || []);
        }
      } catch (err) {
      }

      // 건축 요소 가져오기 (localStorage에서)
      try {
        if (currentRoomId) {
          const savedArchitecture = localStorage.getItem(`architecture_${currentRoomId}`);
          if (savedArchitecture) {
            const architectureData = JSON.parse(savedArchitecture);
            setArchitectureElements(architectureData || []);
          } else {
            setArchitectureElements([]);
          }
        }
      } catch (err) {
        console.error('건축 요소 로드 실패:', err);
        setArchitectureElements([]);
      }

    } catch (err) {
      console.error('데이터 로드 실패:', err);
      setError(err instanceof Error ? err.message : '데이터를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleSeatClick = (seat: Seat) => {
    if (!seat.is_available) return;
    setSelectedSeat(seat);
    setShowActionModal(true);
    if (onSeatSelect) {
      onSeatSelect(seat);
    }
  };

  // 좌석 체크인/예약 처리
  const handleSeatAction = async (action: 'checkin' | 'reserve') => {
    if (!selectedSeat || !currentUser) {
      alert('로그인이 필요합니다.');
      return;
    }

    try {
      // VIP 좌석 체크
      const seatNum = parseInt(selectedSeat.seat_number.toString().match(/\d+/)?.[0] || '0');
      try {
        const vipCheckResponse = await fetch(`/api/v1/vip-check/check/${selectedSeat.seat_number}`);
        const vipData = await vipCheckResponse.json();
        
        if (vipData.is_vip && !vipData.can_reserve) {
          alert('이 좌석은 VIP 회원 전용 좌석입니다.');
          return;
        }
      } catch (vipError) {
      }

      const zone = zones.find(z => z.id === selectedSeat.zone_id);
      const zoneName = zone?.name || '구역';

      if (action === 'checkin') {
        // 직접 체크인 API 호출 (예약 단계 생략)
        const checkInResponse = await fetch('/api/student-seats/check-in', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            seat_number: selectedSeat.seat_number,
          })
        });

        if (!checkInResponse.ok) {
          const errorData = await checkInResponse.json();
          throw new Error(errorData.detail || '체크인에 실패했습니다.');
        }

        const checkInData = await checkInResponse.json();

        // 세션 설정
        const session = {
          id: checkInData.session_id,
          user_id: currentUser.id,
          seat_id: checkInData.seat_number,
          check_in_at: checkInData.check_in_time,
          total_minutes: 0,
          study_minutes: 0,
          break_minutes: 0,
          status: 'active' as const,
          minutes_deducted: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        setCurrentSession(session);
        alert(`${zoneName} ${selectedSeat.seat_number}번 좌석에 입실되었습니다.`);
        navigate('/dashboard');
      } else if (action === 'reserve') {
        // 예약 기능은 일단 비활성화 (API 경로 불일치)
        alert('예약 기능은 현재 개발 중입니다. 직접 입실을 이용해주세요.');
        return;
      }
    } catch (error) {
      console.error('Seat action failed:', error);
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
      alert(
        `${action === 'checkin' ? '입실' : '예약'} 처리 중 오류가 발생했습니다:\n${errorMessage}`
      );
    } finally {
      setShowActionModal(false);
      setSelectedSeat(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">데이터를 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex flex-col max-w-full">
        {/* 헤더 - 고정 */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">좌석 선택</h1>
              <p className="text-gray-600 mt-1">
                원하는 좌석을 선택하세요
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-500">
                사용 가능한 좌석: {seats.filter(s => s.is_available && !s.is_out_of_order).length}개
              </div>
              <button
                onClick={loadData}
                className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                🔄 새로고침
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 p-6 flex-1 min-h-0">
          {/* 중앙: 레이아웃 뷰 */}
          <div className="flex-1 min-w-0 min-h-0 flex flex-col order-2 lg:order-1">
            <div className="bg-white rounded-lg shadow overflow-hidden flex-1 min-h-0 flex flex-col">
              <div className="p-4 bg-gray-50 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">
                    {rooms.find(r => r.id === selectedRoom)?.name || ''} 레이아웃
                  </h3>
                  <div className="text-sm text-gray-500">
                    좌석: {seats.length}개 | 
                    기기: {devices.length}개 | 
                    구역: {zones.length}개
                  </div>
                </div>
                
                {/* 컨트롤 바 */}
                <div className="flex items-center gap-2 mt-3">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setScale(Math.max(0.5, scale - 0.1))}
                      className="p-1 hover:bg-gray-200 rounded"
                      title="축소"
                    >
                      <MagnifyingGlassMinusIcon className="w-4 h-4" />
                    </button>
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded min-w-12 text-center">
                      {Math.round(scale * 100)}%
                    </span>
                    <button
                      onClick={() => setScale(Math.min(2, scale + 0.1))}
                      className="p-1 hover:bg-gray-200 rounded"
                      title="확대"
                    >
                      <MagnifyingGlassPlusIcon className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="h-4 border-r border-gray-300"></div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setViewMode('all')}
                      className={`px-3 py-1 text-xs rounded ${
                        viewMode === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      전체
                    </button>
                    <button
                      onClick={() => setViewMode('seats')}
                      className={`px-3 py-1 text-xs rounded ${
                        viewMode === 'seats' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      좌석만
                    </button>
                    <button
                      onClick={() => setViewMode('devices')}
                      className={`px-3 py-1 text-xs rounded ${
                        viewMode === 'devices' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      기기만
                    </button>
                  </div>
                </div>
              </div>

              {/* 스크롤 가능한 레이아웃 영역 */}
              <div className="flex-1 overflow-auto bg-gray-100 relative">
                <div 
                  className="relative bg-white border-2 border-dashed border-gray-300 m-4"
                  style={{
                    width: `${(rooms.find(r => r.id === selectedRoom)?.grid_width || 20) * CELL_SIZE * scale}px`,
                    height: `${(rooms.find(r => r.id === selectedRoom)?.grid_height || 15) * CELL_SIZE * scale}px`,
                    transform: `scale(${scale})`,
                    transformOrigin: '0 0',
                  }}
                >
                  {/* 격자 배경 */}
                  <div className="absolute inset-0 opacity-20">
                    {Array.from({ length: rooms.find(r => r.id === selectedRoom)?.grid_height || 15 }).map((_, row) =>
                      Array.from({ length: rooms.find(r => r.id === selectedRoom)?.grid_width || 20 }).map((_, col) => (
                        <div
                          key={`grid-${row}-${col}`}
                          className="absolute border border-gray-200"
                          style={{
                            left: `${col * CELL_SIZE}px`,
                            top: `${row * CELL_SIZE}px`,
                            width: `${CELL_SIZE}px`,
                            height: `${CELL_SIZE}px`,
                          }}
                        />
                      ))
                    )}
                  </div>

                  {/* 건축 요소 렌더링 */}
                  {(viewMode === 'all' || viewMode === 'devices') && architectureElements
                    .filter(element => element.room_id === selectedRoom)
                    .map(element => {
                      const position = {
                        x: element.position_x,
                        y: element.position_y
                      };
                      
                      return (
                        <div
                          key={element.id}
                          className="absolute pointer-events-none"
                          style={{
                            left: `${position.x}px`,
                            top: `${position.y}px`,
                            width: `${element.width}px`,
                            height: `${element.height}px`,
                            transform: `rotate(${element.rotation || 0}deg)`,
                            zIndex: element.element_type === 'pillar' ? 20 : 10,
                          }}
                        >
                          {renderArchitectureElement(element)}
                        </div>
                      );
                    })}

                  {/* 좌석 렌더링 */}
                  {(viewMode === 'all' || viewMode === 'seats') && seats
                    .filter(seat => seat.room_id === selectedRoom)
                    .map(seat => {
                      const zone = zones.find(z => z.id === seat.zone_id);
                      const zoneColor = zone?.color || '#666666';
                      const isSelected = selectedSeat?.seat_number === seat.seat_number;
                      
                      return (
                        <div
                          key={`seat-${seat.seat_number}`}
                          className={`absolute transition-all cursor-pointer hover:scale-105 ${
                            isSelected ? 'filter drop-shadow-xl ring-4 ring-blue-500 ring-opacity-50' : ''
                          } ${
                            !seat.is_available ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          style={{
                            left: `${seat.position_x * CELL_SIZE}px`,
                            top: `${seat.position_y * CELL_SIZE}px`,
                            width: `${CELL_SIZE}px`,
                            height: `${CELL_SIZE}px`,
                            zIndex: 30,
                          }}
                          onClick={() => handleSeatClick(seat)}
                        >
                          <div className={`relative w-full h-full ${getSeatRotation(seat.direction)}`}>
                            {/* 책상 */}
                            <div 
                              className="absolute w-16 h-8 rounded-lg transform"
                              style={{ 
                                backgroundColor: seat.is_available ? zoneColor : '#9CA3AF',
                                filter: seat.is_available ? 'brightness(0.9)' : 'brightness(0.7)',
                                top: '0',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1), inset 0 1px 2px rgba(255,255,255,0.2)'
                              }}
                            >
                              <div className="absolute inset-0 rounded-t-lg" 
                                   style={{ 
                                     background: 'linear-gradient(to bottom, rgba(255,255,255,0.15), transparent)',
                                   }}
                              />
                            </div>
                            
                            {/* 의자 */}
                            <div 
                              className="absolute w-12 h-6 rounded-lg transform"
                              style={{ 
                                backgroundColor: seat.is_available ? zoneColor : '#9CA3AF',
                                filter: seat.is_available ? 'brightness(0.8)' : 'brightness(0.6)',
                                top: '32px',
                                left: '2px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}
                            />
                            
                            {/* 좌석 번호 */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span 
                                className="text-xs font-bold text-white drop-shadow-lg"
                                style={{ 
                                  textShadow: '1px 1px 2px rgba(0,0,0,0.7)' 
                                }}
                              >
                                {seat.seat_number}
                              </span>
                            </div>
                            
                            {/* 사용 불가 표시 */}
                            {!seat.is_available && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <XMarkIcon className="w-8 h-8 text-red-500" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                  {/* 기기 렌더링 */}
                  {(viewMode === 'all' || viewMode === 'devices') && devices
                    .filter(device => device.room_id === selectedRoom)
                    .map(device => {
                      return (
                        <div
                          key={`device-${device.id}`}
                          className={`absolute p-2 border-2 rounded-lg flex flex-col items-center justify-center pointer-events-none ${
                            getDeviceStatusColor(device.status)
                          }`}
                          style={{
                            left: `${device.position_x * CELL_SIZE}px`,
                            top: `${device.position_y * CELL_SIZE}px`,
                            width: `${CELL_SIZE}px`,
                            height: `${CELL_SIZE}px`,
                            zIndex: 40,
                          }}
                        >
                          {getDeviceIcon(device.device_type)}
                          <span className="text-xs mt-1 text-center font-medium">
                            {device.device_name.length > 6 
                              ? device.device_name.substring(0, 6) + '...' 
                              : device.device_name
                            }
                          </span>
                          {/* 상태 인디케이터 */}
                          <div className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                            device.status === 'online' ? 'bg-green-500' :
                            device.status === 'warning' ? 'bg-yellow-500' :
                            device.status === 'error' ? 'bg-red-500' :
                            'bg-gray-500'
                          }`} />
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>

          {/* 우측: 정보 패널 */}
          <div className="w-full lg:w-80 order-1 lg:order-2">
            <div className="bg-white rounded-lg shadow p-6">
              {selectedSeat ? (
                <div>
                  <h3 className="text-lg font-medium mb-4">선택된 좌석</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-600">좌석 번호</label>
                      <p className="font-medium text-lg">{selectedSeat.seat_number}번</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">구역</label>
                      <p className="font-medium">
                        {zones.find(z => z.id === selectedSeat.zone_id)?.name || '미지정'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">상태</label>
                      <p className={`font-medium ${
                        selectedSeat.is_available ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {selectedSeat.is_available ? '사용 가능' : '사용 중'}
                      </p>
                    </div>
                  </div>
                  
                  {selectedSeat.is_available && (
                    <div className="mt-6 space-y-3">
                      <button 
                        onClick={() => handleSeatAction('checkin')}
                        className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        이 좌석으로 입실하기
                      </button>
                      <button 
                        onClick={() => handleSeatAction('reserve')}
                        className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium"
                      >
                        좌석 예약하기
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-500">
                  <div className="mb-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircleIcon className="w-8 h-8 text-gray-400" />
                    </div>
                  </div>
                  <h3 className="font-medium mb-2">좌석을 선택하세요</h3>
                  <p className="text-sm">
                    레이아웃에서 원하는 좌석을 클릭하면<br />
                    자세한 정보를 확인할 수 있습니다.
                  </p>
                </div>
              )}
            </div>

            {/* 범례 */}
            <div className="bg-white rounded-lg shadow p-6 mt-4">
              <h3 className="font-medium mb-4">범례</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-green-600 rounded"></div>
                  <span>사용 가능한 좌석</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-gray-400 rounded"></div>
                  <span>사용 중인 좌석</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-blue-600 rounded ring-2 ring-blue-300"></div>
                  <span>선택된 좌석</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-yellow-200 border border-gray-400"></div>
                  <span>출입구</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-gray-700 rounded"></div>
                  <span>기둥</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentLayoutView;